// All imports at the top…
import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFirestore } from "../../firebase/FirestoreContext";
import { doc, setDoc, getDoc } from "firebase/firestore";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ReactQuill, { Quill } from "react-quill"; // Import Quill from react-quill
import "react-quill/dist/quill.snow.css";
import { EnvContext } from "../../../context/EnvContext";
import config from "../../../config";

// Import image uploader and image resize modules.
import ImageUploader from "quill-image-uploader";
import ImageResize from "quill-image-resize-module-react";

// --- Register ImageResize and ImageUploader FIRST ---
Quill.register("modules/imageResize", ImageResize);
Quill.register("modules/imageUploader", ImageUploader);

// Import Quill formats directly via Quill.import.
const Font = Quill.import("formats/font");
const Align = Quill.import("formats/align");
const Color = Quill.import("formats/color");
const Background = Quill.import("formats/background");

// Optional: whitelist fonts as needed.
Font.whitelist = ["sans-serif", "serif", "monospace"];

// Register formats.
Quill.register(Font, true);
Quill.register(Align, true);
Quill.register(Color, true);
Quill.register(Background, true);

// --- Patch the Image Resize Module to suppress errors ---
// Here we patch the onImageClick method (or whichever method is triggering the error)
// so that any error (such as "undefined is not an object (evaluating 'new d.Attributor')")
// is caught and logged rather than propagated.
const ImageResizeModule = Quill.import("modules/imageResize");
if (ImageResizeModule && ImageResizeModule.prototype && ImageResizeModule.prototype.onImageClick) {
  const originalOnImageClick = ImageResizeModule.prototype.onImageClick;
  ImageResizeModule.prototype.onImageClick = function (e) {
    try {
      originalOnImageClick.call(this, e);
    } catch (err) {
      console.warn("Suppressed image resize error:", err);
      // Do nothing, just suppress the error.
    }
  };
}

// Define the page identifier.
const page = "HelpCenter";

/**
 * Converts a file to a Base64 data URL.
 * @param {File} file - The image file selected by the user.
 * @returns {Promise<string>} - Resolves with a base64 data URL.
 */
const uploadImageAsBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      resolve(e.target.result);
    };
    reader.onerror = (error) => {
      console.error("Error reading file:", error);
      reject("Error reading file");
    };
    reader.readAsDataURL(file);
  });
};

// Configure ReactQuill modules.
const modules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["image", "code-block"], // Ensure 'image' is in the toolbar
    [{ font: Font.whitelist }], // Add font dropdown
    [{ align: [] }], // Add alignment options
    [{ color: [] }, { background: [] }], // Add color options
    ["clean"],
  ],
  imageUploader: {
    upload: (file) => uploadImageAsBase64(file),
  },
  imageResize: {
    modules: ["Resize", "DisplaySize", "Toolbar"],
  },
};

const ManagePage = () => {
  const navigate = useNavigate();
  const environment = useContext(EnvContext);
  const db = useFirestore();

  const collectionPath = environment === "staging" ? config.enveironment.staging.collectionPath : config.enveironment.production.collectionPath;

  const [pageContent, setPageContent] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchPageContent = async () => {
      try {
        const docRef = doc(db, `${collectionPath}Pages`, page);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          setPageContent(data.page_content || "");
        } else {
          toast.info("Page does not exist yet. You can create it by saving.");
          setPageContent("");
        }
      } catch (error) {
        console.error("Error fetching page content:", error);
        toast.error("Failed to fetch page content.");
      }
    };

    fetchPageContent();
  }, [db, collectionPath]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const updatedContent = { page_content: pageContent };
      const pageRef = doc(db, `${collectionPath}Pages`, page);
      await setDoc(pageRef, updatedContent, { merge: true });
      toast.success("Page updated successfully!");
      // Optionally: navigate("/main/pages");
    } catch (error) {
      console.error("Error updating page:", error);
      toast.error("Error updating page!");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <ToastContainer position="top-center" autoClose={5000} hideProgressBar />
      <h2 style={styles.title}>Manage Page: {page}</h2>
      <form onSubmit={handleSubmit} style={styles.form}>
        <div style={styles.formElement}>
          <label style={styles.formLabel}>Page Content:</label>
          <ReactQuill theme="snow" value={pageContent} onChange={setPageContent} modules={modules} />
        </div>
        <div style={styles.buttonGroup}>
          <button type="submit" style={styles.button} disabled={loading}>
            {loading ? "Saving..." : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
};

const styles = {
  title: { fontWeight: "200", paddingBottom: "10px" },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    padding: "20px",
    border: "1px solid #ccc",
    borderRadius: "5px",
    backgroundColor: "#f9f9f9",
  },
  formElement: {
    display: "flex",
    flexDirection: "column",
    gap: "5px",
  },
  formLabel: { fontWeight: "bold", fontSize: "14px", color: "#4a4141" },
  buttonGroup: { display: "flex", gap: "10px" },
  button: {
    backgroundColor: "#313942",
    color: "white",
    padding: "10px 20px",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
  },
  cancelButton: {
    backgroundColor: "#ccc",
    color: "black",
    padding: "10px 20px",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
  },
};

export default ManagePage;
